"use client";

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
  trackPageView,
  trackNavigation,
  trackProject,
  trackEngagement,
  trackContact,
  trackPerformance,
  throttle,
  getCurrentPagePath,
  getPageTitle,
  analyticsConfig,
} from '@/utils/analytics';

export interface UseAnalyticsReturn {
  trackNavigation: typeof trackNavigation;
  trackProject: typeof trackProject;
  trackEngagement: typeof trackEngagement;
  trackContact: typeof trackContact;
  trackPerformance: typeof trackPerformance;
}

export const useAnalytics = (): UseAnalyticsReturn => {
  const pathname = usePathname();

  useEffect(() => {
    const pageTitle = getPageTitle();
    const pageUrl = getCurrentPagePath();
    
    trackPageView(pageUrl, pageTitle);
    
    const loadTime = performance.now();
    trackPerformance('page_load', pageTitle, { loadTime });
  }, [pathname]);

  return {
    trackNavigation,
    trackProject,
    trackEngagement,
    trackContact,
    trackPerformance,
  };
};

export const useScrollDepthTracking = () => {
  const scrollDepthTracked = useRef<Set<number>>(new Set());

  useEffect(() => {
    const handleScrollDepth = throttle(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

      analyticsConfig.scrollDepth.thresholds.forEach((threshold) => {
        if (scrollPercentage >= threshold && !scrollDepthTracked.current.has(threshold)) {
          scrollDepthTracked.current.add(threshold);
          trackEngagement('scroll_depth', `${threshold}%`, {
            scrollPercentage: threshold,
            pageUrl: getCurrentPagePath(),
          });
        }
      });
    }, 250);

    window.addEventListener('scroll', handleScrollDepth, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollDepth);
  }, []);
};

export const useSectionTracking = (
  elementRef: React.RefObject<HTMLElement | null>,
  sectionName: string,
  threshold: number = 0.5
) => {
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackEngagement('section_view', sectionName, {
              sectionName,
              pageUrl: getCurrentPagePath(),
            });
          }
        });
      },
      { threshold }
    );

    observer.observe(element);
    return () => observer.unobserve(element);
  }, [elementRef, sectionName, threshold]);
};

export const usePerformanceTracking = () => {
  useEffect(() => {
    const startTime = performance.now();

    const trackLoadComplete = () => {
      const loadTime = performance.now() - startTime;
      trackPerformance('page_load', getPageTitle(), { loadTime });
    };

    if (document.readyState === 'complete') {
      trackLoadComplete();
    } else {
      window.addEventListener('load', trackLoadComplete);
      return () => window.removeEventListener('load', trackLoadComplete);
    }
  }, []);
};